/**
 * profileTier — the boot checks' one read of this device's profile row
 * (open-access lockdown): tier, and whether this session still owns it.
 *
 * Another session's row is no longer readable directly, yet the boot checks
 * exist precisely for that case (converted on another device, merged, or this
 * session expired and needs the text-code login to relink). The server's
 * profile_tier_check answers without handing back any data.
 *
 * The result keeps the { account_status, auth_user_id } shape the tier rules
 * already take (reconcileTierWithRow, the expired-session check):
 * auth_user_id is this session's id when mine, NOT_THIS_SESSION when another
 * session owns it, null when nobody does.
 */

import { supabase, getAuthUserId } from '../supabase';

export const NOT_THIS_SESSION = 'another-session';

/**
 * @param {string} profileUserId
 * @returns {Promise<{row: {account_status: string|null, auth_user_id: string|null, isMine: boolean}|null, error: Object|null}>}
 *   row null = no such profile
 */
export const readProfileTier = async (profileUserId) => {
  const { data, error } = await supabase
    .rpc('profile_tier_check', { p_profile_user_id: profileUserId })
    .maybeSingle();
  if (error) return { row: null, error };
  if (!data) return { row: null, error: null };
  let owner = null;
  if (data.is_mine) owner = getAuthUserId();
  else if (data.has_owner) owner = NOT_THIS_SESSION;
  return {
    row: { account_status: data.account_status, auth_user_id: owner, isMine: !!data.is_mine },
    error: null,
  };
};
